import type {
  MetricCatalogMetricEntry,
  MetricCatalogModuleEntry,
  MetricCatalogResponse,
  MetricCatalogResponseEntry,
  MetricCatalogScoreTypeEntry,
} from "@/interfaces/v2Analytics";
import {
  labelVariants,
  panelTextVariants,
  type MarketTextVariant,
} from "@/utils/catalogLabels";
import { isAiVisibilityModule } from "@/utils/aiVisibility/isAiVisibilityModule";

/**
 * Defensive: config.module is always "IMAGERY" for the emotional screen
 * (emotional.config.ts:46), but userSelection.module can carry "EMOTIONAL";
 * normalize so any caller vocabulary works. Do not write a test that tries to
 * reach this via config.module — it can't.
 *
 * Same idea for AI Visibility: `screenConfig.module` carries "AI_VISIBILITY"
 * (aivisibility.config.ts), which lowercases to "ai_visibility" -- the
 * catalog's own moduleKey -- but `ViewDefinition.navigation.module` (the
 * stored/URL vocabulary, see viewDefinition/types.ts's ViewModule) spells it
 * "aivisibility", no underscore. Without this alias, a caller using the
 * stored spelling (e.g. the view-details panel's metricLabel resolver) can
 * never find the module's catalog entry, even though the same metric
 * resolves fine wherever screenConfig.module is used instead.
 */
function normalizeModuleKey(module: string): string {
  const m = module.toLowerCase();
  if (m === "emotional") return "imagery";
  // App / screenConfig: AI_VISIBILITY; ViewDefinition: aivisibility;
  // catalog moduleKey: ai_visibility.
  if (isAiVisibilityModule(module)) return "ai_visibility";
  return m;
}

export function getModuleEntry(
  catalog: MetricCatalogResponse | null,
  module: string,
): MetricCatalogModuleEntry | undefined {
  if (!catalog || !module) return undefined;
  const key = normalizeModuleKey(module);
  return catalog.modules.find((m) => m.moduleKey.toLowerCase() === key);
}

export function getMetricEntry(
  catalog: MetricCatalogResponse | null,
  module: string,
  metricKey: string | null,
): MetricCatalogMetricEntry | undefined {
  if (!metricKey) return undefined;
  return getModuleEntry(catalog, module)?.metrics.find(
    (m) => m.metricKey.toLowerCase() === metricKey.toLowerCase(),
  );
}

/**
 * The markets whose opinion on this metric's wording actually counts: the
 * selection intersected with the markets that DEFINE the metric.
 *
 * Without this, a selected market that does not offer the metric contributes
 * the canonical value (it has no override) and manufactures a false conflict.
 * An empty intersection resolves canonically.
 */
function relevantMarkets(
  entry: MetricCatalogMetricEntry,
  marketIds: number[],
): number[] {
  // Runtime catalogs (esp. stale bootstrap cache) can omit marketIds.
  return marketIds.filter((id) => (entry.marketIds ?? []).includes(id));
}

/**
 * Per-market variants of this metric's display label across the selection.
 * One variant → every relevant market agrees. Several → genuine divergence.
 * `[]` when the module/metric is not in the catalog.
 */
export function getMetricLabelVariants(
  catalog: MetricCatalogResponse | null,
  module: string,
  metricKey: string | null,
  marketIds: number[],
): MarketTextVariant[] {
  const entry = getMetricEntry(catalog, module, metricKey);
  if (!entry) return [];
  return labelVariants(entry, relevantMarkets(entry, marketIds));
}

/**
 * Per-market variants of this metric's question wording (falling back to its
 * description) across the selection. One variant → every relevant market was
 * asked the same thing. Several → they were asked different things.
 * `[]` when the module/metric is not in the catalog.
 */
export function getMetricPanelTextVariants(
  catalog: MetricCatalogResponse | null,
  module: string,
  metricKey: string | null,
  marketIds: number[],
): MarketTextVariant[] {
  const entry = getMetricEntry(catalog, module, metricKey);
  if (!entry) return [];
  return panelTextVariants(entry, relevantMarkets(entry, marketIds));
}

/** Catalog-ordered metrics defined in ≥1 selected market. Empty selection → all. */
export function getModuleMetrics(
  catalog: MetricCatalogResponse | null,
  module: string,
  marketIds: number[],
): MetricCatalogMetricEntry[] {
  const metrics = getModuleEntry(catalog, module)?.metrics ?? [];
  if (marketIds.length === 0) return metrics;
  // Missing / empty marketIds = unknown applicability (stale bootstrap catalog).
  // Treat as available in all markets so selection + data fetches are not blocked.
  return metrics.filter((m) => {
    const ids = m.marketIds;
    if (ids == null || ids.length === 0) return true;
    return ids.some((id) => marketIds.includes(id));
  });
}

/** Insertion order IS menu order — never sort. */
export function getScoreTypeOptions(
  entry: MetricCatalogMetricEntry | undefined,
): { key: string; entry: MetricCatalogScoreTypeEntry }[] {
  if (!entry?.scoreTypes) return [];
  return Object.entries(entry.scoreTypes).map(([key, e]) => ({ key, entry: e }));
}

/**
 * Union across the module's market-eligible metrics, first-seen order (drives
 * the PIT method menu). Market-filtered by the same rule as `getModuleMetrics`:
 * a metric counts when it is offered in ≥1 selected market; empty selection →
 * all metrics.
 */
export function getModuleScoreTypeOptions(
  catalog: MetricCatalogResponse | null,
  module: string,
  marketIds: number[],
): { key: string; entry: MetricCatalogScoreTypeEntry }[] {
  const seen = new Map<string, MetricCatalogScoreTypeEntry>();
  for (const metric of getModuleMetrics(catalog, module, marketIds)) {
    for (const [key, e] of Object.entries(metric.scoreTypes ?? {})) {
      if (!seen.has(key)) seen.set(key, e);
    }
  }
  return Array.from(seen, ([key, entry]) => ({ key, entry }));
}

/**
 * Score Type control renders iff true (derived, never configured). Also gates
 * module-level response logic: single-method modules never carry a response in
 * PIT/PRE_POST — per-metric catalog defaults apply server-side.
 */
export function moduleHasMultipleMethods(
  catalog: MetricCatalogResponse | null,
  module: string,
  marketIds: number[],
): boolean {
  return getModuleScoreTypeOptions(catalog, module, marketIds).length > 1;
}

/**
 * Module landing default = the FIRST market-eligible catalog metric's
 * defaultScoreType (reputation → QRQ → MEAN; reputation in market 40, where only
 * `quality` is offered → PERCENTAGE).
 */
export function getModuleDefaultScoreType(
  catalog: MetricCatalogResponse | null,
  module: string,
  marketIds: number[],
): string | null {
  return getModuleMetrics(catalog, module, marketIds)[0]?.defaultScoreType ?? null;
}

export function getResponses(
  entry: MetricCatalogMetricEntry | undefined,
  scoreType: string | null,
): MetricCatalogResponseEntry[] | undefined {
  if (!entry || !scoreType || !entry.scoreTypes) return undefined;
  return entry.scoreTypes[scoreType]?.responses;
}

export function getDefaultResponse(
  entry: MetricCatalogMetricEntry | undefined,
  scoreType: string | null,
): string | null {
  if (!entry || !scoreType || !entry.scoreTypes) return null;
  return entry.scoreTypes[scoreType]?.defaultResponse ?? null;
}

/**
 * PIT/Pre-Post response menu: the first module metric carrying responses under
 * the method. Module AGGREGATE vocabularies are uniform today; assert in dev so
 * a future divergent catalog fails loudly instead of rendering a wrong menu.
 */
export function getModuleResponses(
  catalog: MetricCatalogResponse | null,
  module: string,
  scoreType: string | null,
  marketIds: number[],
): MetricCatalogResponseEntry[] | undefined {
  if (!scoreType) return undefined;
  const metrics = getModuleMetrics(catalog, module, marketIds);
  const carriers = metrics
    .map((m) => m.scoreTypes?.[scoreType]?.responses)
    .filter((r): r is MetricCatalogResponseEntry[] => r != null);
  if (carriers.length === 0) return undefined;
  if (import.meta.env.DEV) {
    const key = (rs: MetricCatalogResponseEntry[]) =>
      rs.filter((r) => r.kind === "AGGREGATE").map((r) => r.response).join(",");
    const first = key(carriers[0]);
    if (carriers.some((rs) => key(rs) !== first)) {
      const msg = `[metricsCatalog] module "${module}" metrics disagree on AGGREGATE responses under ${scoreType}`;
      // Vitest runs with DEV truthy: throw under test so a divergent catalog
      // fails loudly; log-only in the running app.
      if (import.meta.env.MODE === "test") throw new Error(msg);
      // eslint-disable-next-line no-console
      console.error(msg);
    }
  }
  return carriers[0];
}

export function getModuleDefaultResponse(
  catalog: MetricCatalogResponse | null,
  module: string,
  scoreType: string | null,
  marketIds: number[],
): string | null {
  if (!scoreType) return null;
  for (const m of getModuleMetrics(catalog, module, marketIds)) {
    const d = m.scoreTypes?.[scoreType]?.defaultResponse;
    if (d != null) return d;
  }
  return null;
}

// The per-market label rule lives in `@/utils/catalogLabels` — the distribution
// catalog carries the same wire shape and resolves through the same function.
